import { useCallback, useEffect, useState } from 'react';
import {
  approveJoinRequest,
  fetchIncomingJoinRequests,
  rejectJoinRequest,
} from '../../api/company';
import { useCompanyStore } from '../../store/companyStore';
import { JoinRequest } from '../../types/company';
import './JoinRequestPanel.css';

import { reportError } from '../../utils/notify';
function JoinRequestPanel() {
  const role = useCompanyStore((state) => state.role);
  const companyId = useCompanyStore((state) => state.companyId);
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<number | null>(null);

  const isOwner = role === 'OWNER';

  const loadRequests = useCallback(async () => {
    if (!isOwner) {
      setRequests([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const list = await fetchIncomingJoinRequests();
      setRequests(list);
      setError('');
    } catch {
      reportError(setError, '참가 요청 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, companyId]);

  const handleApprove = async (joinRequestId: number) => {
    try {
      setActingId(joinRequestId);
      await approveJoinRequest(joinRequestId);
      setRequests((prev) => prev.filter((item) => item.joinRequestId !== joinRequestId));
    } catch {
      reportError(setError, '승인에 실패했습니다.');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (joinRequestId: number) => {
    try {
      setActingId(joinRequestId);
      await rejectJoinRequest(joinRequestId);
      setRequests((prev) => prev.filter((item) => item.joinRequestId !== joinRequestId));
    } catch {
      reportError(setError, '거부에 실패했습니다.');
    } finally {
      setActingId(null);
    }
  };

  return (
    <section className="ec-widget join-panel">
      <div className="ec-widget__header">
        <div className="ec-list-widget__title">
          <strong>가입요청</strong>
          <span className="join-panel__sub">회사 참가 요청 관리</span>
        </div>
        <div className="ec-widget__actions">
          <button type="button" className="ec-tool-btn" title="새로고침" onClick={loadRequests}>
            ↻
          </button>
        </div>
      </div>

      <div className="join-panel__body">
        {!isOwner && (
          <p className="join-panel__hint">
            소유자(OWNER)만 참가 요청을 확인하고 승인/거부할 수 있습니다.
          </p>
        )}

        {isOwner && loading && <p className="join-panel__hint">불러오는 중...</p>}

        {isOwner && !loading && requests.length === 0 && (
          <p className="join-panel__hint">대기 중인 참가 요청이 없습니다.</p>
        )}

        {isOwner && requests.length > 0 && (
          <table className="ec-table join-panel__table">
            <thead>
              <tr>
                <th>이름</th>
                <th>요청일시</th>
                <th style={{ width: '160px' }}>처리</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.joinRequestId}>
                  <td>{request.name || request.nickname || '-'}</td>
                  <td>{formatDateTime(request.createdAt)}</td>
                  <td>
                    <div className="join-panel__actions">
                      <button
                        type="button"
                        className="join-panel__approve"
                        disabled={actingId === request.joinRequestId}
                        onClick={() => handleApprove(request.joinRequestId)}
                      >
                        확인
                      </button>
                      <button
                        type="button"
                        className="join-panel__reject"
                        disabled={actingId === request.joinRequestId}
                        onClick={() => handleReject(request.joinRequestId)}
                      >
                        거부
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {error && <p className="join-panel__error">{error}</p>}
      </div>
    </section>
  );
}

function formatDateTime(value: string) {
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch {
    return value;
  }
}

export default JoinRequestPanel;
